import type { Parameters } from '@medplum/fhirtypes';

import { medplum } from '@/config/medplum';
import { queryClient } from '@/config/queryClient';

export const APP_PERMISSIONS = {
	SUPERADMIN: '*',
	MANAGE_USERS: 'manage_users',
	MANAGE_ENTITIES: 'manage_entities',
	ADMIN_DASHBOARD: 'admin_dashboard',
	ENROLLMENT_ADMIN: 'enrollment_admin',
	PATIENT_ENROLLMENT: 'patient_enrollment',
	CARE_ADMIN: 'care_admin',
	PATIENT_CARE: 'patient_care',
	VIEW_DASHBOARD: 'view_dashboard',
	EDIT_TASKS: 'edit_tasks',
	VIEW_REPORTS: 'view_reports',
} as const;

export type AppPermission = (typeof APP_PERMISSIONS)[keyof typeof APP_PERMISSIONS];

export type PermissionsResponse = {
	role: string;
	permissions: string[];
};

export const permissionsQueryKey = (practitionerId: string | undefined) => [
	'user-permissions',
	practitionerId,
];

export function parsePermissionsResponse(params: Parameters): PermissionsResponse {
	const parameters = params.parameter ?? [];
	const role = parameters.find((p) => p.name === 'role')?.valueString ?? '';
	const permissions = parameters
		.filter((p) => p.name === 'permission')
		.map((p) => p.valueString ?? '')
		.filter(Boolean);
	return { role, permissions };
}

export async function getUserPermissions(): Promise<PermissionsResponse> {
	const profile = medplum.getProfile();
	if (!profile || profile.resourceType !== 'Practitioner') {
		return { role: '', permissions: [] };
	}

	return queryClient.ensureQueryData({
		queryKey: permissionsQueryKey(profile.id),
		queryFn: async () => {
			const result = (await medplum.get(
				medplum.fhirUrl('User', '$get-permissions').toString(),
			)) as Parameters;
			return parsePermissionsResponse(result);
		},
	});
}

export function hasPermission(permissions: string[], permission: AppPermission): boolean {
	return permissions.includes(APP_PERMISSIONS.SUPERADMIN) || permissions.includes(permission);
}
